import { useEffect, useState } from "react";

export class ApiError extends Error {}

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  const response = await fetch(`/api${path}`, {
    ...init,
    headers: { "Content-Type": "application/json", ...init?.headers },
  });

  const body: unknown = await response.json().catch(() => null);

  if (!response.ok) {
    const message =
      body && typeof body === "object" && "error" in body
        ? String((body as { error: unknown }).error)
        : `Request failed (${response.status})`;
    throw new ApiError(message);
  }

  return body as T;
}

export const api = {
  get: <T,>(path: string) => request<T>(path),
  post: <T,>(path: string, data: unknown) =>
    request<T>(path, { method: "POST", body: JSON.stringify(data) }),
  put: <T,>(path: string, data: unknown) =>
    request<T>(path, { method: "PUT", body: JSON.stringify(data) }),
  delete: <T,>(path: string) => request<T>(path, { method: "DELETE" }),
};

/**
 * Multipart upload — bypasses `request()`'s JSON content-type, since the
 * browser must set its own multipart boundary on the header itself.
 */
export async function uploadFile<T>(
  path: string,
  file: File,
  /** Extra text fields sent alongside the file — the chapter mapping, in
   *  practice. Skipped when null/undefined so the server sees an absent
   *  field rather than the string "null". */
  fields: Record<string, string | null | undefined> = {},
): Promise<T> {
  const formData = new FormData();
  formData.append("file", file);
  for (const [name, value] of Object.entries(fields)) {
    if (value) formData.append(name, value);
  }

  const response = await fetch(`/api${path}`, { method: "POST", body: formData });
  const body: unknown = await response.json().catch(() => null);

  if (!response.ok) {
    const message =
      body && typeof body === "object" && "error" in body
        ? String((body as { error: unknown }).error)
        : `Upload failed (${response.status})`;
    throw new ApiError(message);
  }

  return body as T;
}

export interface AsyncState<T> {
  data: T | null;
  error: string | null;
  loading: boolean;
}

/**
 * Tracks which of a set of independent actions is currently in flight — a
 * row's Pause button, another row's Approve, a bulk action — so a click can
 * disable and spin just the one control it triggered rather than either
 * nothing (most call sites before this existed) or the whole screen.
 *
 * Keyed rather than a single boolean: several rows in the same list can each
 * have their own action running, and conflating them would disable every
 * row's buttons for the duration of any one of them.
 */
export function usePendingActions() {
  const [pending, setPending] = useState<Set<string>>(new Set());

  function isPending(key: string): boolean {
    return pending.has(key);
  }

  /**
   * Runs `fn`, marking `key` pending for its duration. Errors are rethrown
   * after clearing the pending state, so the caller's own try/catch (usually
   * a toast) still runs — this only ever adds the spinner/disable, never
   * changes error handling.
   */
  async function run<T>(key: string, fn: () => Promise<T>): Promise<T> {
    setPending((prev) => new Set(prev).add(key));
    try {
      return await fn();
    } finally {
      setPending((prev) => {
        if (!prev.has(key)) return prev;
        const next = new Set(prev);
        next.delete(key);
        return next;
      });
    }
  }

  return { isPending, run };
}

/**
 * Minimal data-fetching hook. Deliberately not React Query — the MVP needs a
 * handful of reads, and adding a data layer is the kind of premature
 * infrastructure the spec rules out (§43).
 *
 * `background`, when true, marks this refetch as a silent poll tick: `loading`
 * never flips true for it, so a live-status interval hitting the same
 * endpoint every few seconds can't trip a RefetchOverlay's dim — there's
 * nothing for the overlay to react to. A real load (first mount, or deps
 * changing for a reason other than the poll) still reports `loading`
 * normally: first mount has nothing on screen yet to protect, and a
 * user-caused change (a filter, an action, a manual refresh) is worth
 * showing as loading if it's slow. Callers combine this with their own poll
 * tracking (see `PipelineExecutionsList` for the pattern) — recomputed at
 * render, not toggled from an effect, since a ref write doesn't itself
 * schedule the re-render `useApi` needs to see the new value.
 */
export function useApi<T>(
  path: string | null,
  deps: unknown[] = [],
  options?: { background?: boolean },
): AsyncState<T> {
  const [state, setState] = useState<AsyncState<T>>({ data: null, error: null, loading: path !== null });
  const background = options?.background ?? false;

  useEffect(() => {
    if (path === null) {
      setState({ data: null, error: null, loading: false });
      return;
    }

    let cancelled = false;
    // A background poll keeps the current data on screen with no loading
    // flip at all — not even a debounced one. Only the request itself
    // repeats; visually nothing changes until the new rows are in.
    if (!background) {
      setState((previous) => ({ ...previous, loading: true, error: null }));
    }

    api
      .get<T>(path)
      .then((data) => {
        if (!cancelled) setState({ data, error: null, loading: false });
      })
      .catch((error: unknown) => {
        if (!cancelled) {
          // Keep whatever was already on screen. These endpoints are polled, so
          // nulling the data would blank a working table on one failed refresh.
          setState((previous) => ({
            data: previous.data,
            error: error instanceof Error ? error.message : String(error),
            loading: false,
          }));
        }
      });

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps);

  return state;
}
